import React, { useContext, useMemo, useState } from 'react'

import { Modal, Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import { faKey, faStethoscope, faUsers } from '@fortawesome/pro-solid-svg-icons'

import SmartTable, { SmartTableDataTypes } from '../shared/SmartTable'
import UserManagedSectors from './UserManagedSectors'
import UserTutoredSectors from './UserTutoredSectors'
import { validateFormInput, requestWithPromise, isObject } from '../../utils'
import AdminResetPassword from '../shared/AdminResetPassword'
import { DELETED_USER, DUPLICATED_PROFILE, DUPLICATED_USER, getErrorBody, HttpMethods, onError, onSuccess } from '../../utils/apiHelper'
import { buildUpdatedUserFromResponse, deleteUser, pickAffectedProfile, restoreUserByEmail } from '../../utils/api/user'
import { HttpStatuses } from '../../utils/http'
import { EUROPEAN_DATE, InstitutionRoles, SchoolRoles } from '../../utils/constants'
import { isHospitalAdmin, isObserver } from '../../utils/roles'
import { GlobalContext } from '../../Providers/GlobalProvider'
import { InstitutionContext } from '../../Providers/InstitutionProvider'
import { selectLanguages, selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../reducers/UserReducer'
import { getTimezoneLessMoment } from '../../utils/momentjs'

const Users = () => {
  const { isReadOnly } = useContext(GlobalContext)
  const { institution, loading, users, setUsers } = useContext(InstitutionContext)

  const languages = useSelector(selectLanguages)
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [selectedUserSectors, setSelectedUserSectors] = useState(null)
  const [userTutoredSectors, setUserTutoredSectors] = useState(null)
  const [selectedUserPassword, setSelectedUserPassword] = useState(null)
  const [revivalLoading, setRevivalLoading] = useState(false)
  const [deleteModal, setDeleteModal] = useState({ user: null, body: null, footer: false, title: '' })

  const localLanguages = useMemo(() => languages.map(({ code, name }) => ({
    id: code,
    name: t(name)
  })), [languages, t])
  const isAdmin = useMemo(() => isHospitalAdmin(user), [user.roles])
  const userId = useMemo(() => user.id, [user.id])
  const roles = useMemo(() => [
    {
      id: 1,
      name: t('Administrator'),
      apiName: InstitutionRoles.HOSPITAL_ADMIN
    },
    {
      id: 2,
      name: t('Nurse'),
      apiName: InstitutionRoles.NURSE
    },
    {
      id: 3,
      name: t('Validator'),
      apiName: SchoolRoles.VALIDATOR,
      notAllowedInSelect: true
    },
    {
      id: 4,
      name: t(InstitutionRoles.INSTITUTION_GROUP_ADMIN),
      apiName: InstitutionRoles.INSTITUTION_GROUP_ADMIN,
      notAllowedInSelect: true
    },
    {
      id: 5,
      name: t('user.roles.observer'),
      apiName: InstitutionRoles.OBSERVER
    }
  ], [t])
  const columns = useMemo(() => [
    { type: SmartTableDataTypes.ID, key: 'id' },
    {
      type: SmartTableDataTypes.STRING,
      name: t('Email'),
      key: 'email',
      validate: data => validateFormInput('email', data, true),
      disabled: isReadOnly
    },
    {
      type: SmartTableDataTypes.STRING,
      name: t('Lastname'),
      key: 'lastname',
      validate: data => validateFormInput('name', data, true),
      disabled: isReadOnly
    },
    {
      type: SmartTableDataTypes.STRING,
      name: t('Firstname'),
      key: 'firstname',
      validate: data => validateFormInput('name', data, true),
      disabled: isReadOnly
    },
    {
      type: SmartTableDataTypes.SELECT,
      name: t('Language'),
      key: 'language',
      options: localLanguages,
      preventAutoSelectDefaultValue: true,
      validate: data => {
        return data !== null
      },
      disabled: isReadOnly

    },
    {
      type: SmartTableDataTypes.SELECT,
      name: t('Role'),
      key: 'roles',
      options: roles.filter(item => item.notAllowedInSelect !== true),
      disabled: isReadOnly
    },
    {
      type: SmartTableDataTypes.DATE,
      name: t('Last login'),
      key: 'lastLogin',
      format: 'DD/MM/YYYY',
      disabled: true
    }
  ], [t, localLanguages, roles])
  const additionalActions = useMemo(() => {
    const actions = [
      {
        iconName: faStethoscope,
        type: 'primary',
        title: t('Manage care units coordinated by this user'),
        onClick: u => setSelectedUserSectors({ ...u, roles: [roles.find(r => r.id === u.roles)?.apiName] }),
        disabledCallback: u => {
          let disabled = isReadOnly || !u.roles

          if (!disabled) {
            const role = roles.find(r => r.id === u.roles)

            disabled = !role || isObserver({ roles: [role.apiName] })
          }

          return disabled
        }
      },
      {
        iconName: faUsers,
        type: 'primary',
        title: t('manage_care_units_tutored_by_this_user'),
        onClick: u => setUserTutoredSectors({ ...u, roles: [roles.find(r => r.id === u.roles)?.apiName] }),
        disabledCallback: u => {
          if (isObserver(user)) {
            return u.id !== user.id
          }

          if (isHospitalAdmin(user)) {
            const roleUser = roles.find(r => r.id === u.roles)

            return !isObserver({ roles: [roleUser?.apiName] })
          }

          return true
        }
      }
    ]

    if (isAdmin) {
      actions.push({
        iconName: faKey,
        type: 'primary',
        title: t('Reset password'),
        onClick: setSelectedUserPassword,
        disabledCallback: u => u.isExternal,
        titleCallback: u => u.isExternal ? t('This user is external and cannot have its password reset') : ''
      })
    }
    return actions
  }, [isAdmin, roles, t, setSelectedUserSectors, setSelectedUserPassword])
  const data = useMemo(() => {
    const mappedUsers = users.map(u => {
      let userRoleId = null
      // in readOnlyRoleName -> we store the role name, if the role is not allowed
      let readOnlyRoleName = null

      if (u.roles && u.roles.length) {
        const foundRole = roles.find(r => r.apiName === u.roles[0])
        if (foundRole) {
          if (foundRole.notAllowedInSelect) {
            readOnlyRoleName = foundRole.name
          } else {
            userRoleId = foundRole.id
          }
        }
      }

      return {
        id: u.id,
        email: u.email,
        firstname: u.firstname,
        roles: userRoleId,
        rolesReadOnlyName: readOnlyRoleName,
        isExternal: u.isExternal,
        // lastLogin can be missing (a profile that never logged in, e.g. freshly created) or come in
        // an unexpected format: getTimezoneLessMoment then returns null, hence the optional chaining.
        lastLogin: u.lastLogin ? (getTimezoneLessMoment(isObject(u.lastLogin) ? u.lastLogin.date : u.lastLogin)?.format(EUROPEAN_DATE) ?? null) : null,
        lastname: u.lastname,
        language: u.language,
        institutions: [{ id: institution.id }]
      }
    })

    return mappedUsers
  }, [institution, roles, users])

  const handleAddUser = (createdUser, stopTableLoading) => {
    return new Promise((resolve, reject) => {
      const rawRoleId = createdUser.roles
      createdUser.roles = [roles.filter(r => r.id === createdUser.roles)[0].apiName]

      requestWithPromise('/api/users', HttpMethods.POST, createdUser, user, true, true).then(json => {
        // New { account, profiles } contract (every profile of the account): the created profile is
        // identified by its institution + its role, not by "the first unknown id" (which would pick
        // a sibling profile of another organization for a multi-profile account).
        setUsers(oldUsers => {
          const newUser = pickAffectedProfile(json, { knownProfileIds: oldUsers.map(u => u.id), institutionId: institution.id, role: createdUser.roles[0] })
          return newUser ? [...oldUsers, newUser] : oldUsers
        })

        onSuccess(t('user.create.success'))
      }).catch(error => {
        if (error.status === HttpStatuses.CONFLICT) {
          error.body.getReader().read().then(({ value }) => {
            const decoded = new TextDecoder('utf-8').decode(value)
            const json = JSON.parse(decoded)

            if (json.message === DELETED_USER) {
              setDeleteModal({
                footer: true,
                body: (
                  <p style={{ fontWeight: 'bold' }}>
                    <FontAwesomeIcon color='orange' icon='exclamation-circle' />&nbsp;
                    {t('revive.deleted.user', { email: createdUser.email })}
                  </p>
                ),
                title: t('Restore an user ?'),
                user: { ...createdUser, roles: rawRoleId }
              })
            } else if (json.message === DUPLICATED_PROFILE) {
              setDeleteModal({
                footer: false,
                body: (
                  <p style={{ fontWeight: 'bold' }}>
                    <FontAwesomeIcon color='orange' icon='exclamation-circle' />&nbsp;
                    {t('users_view.handle_add_user.duplicated_profile.body')}
                  </p>
                ),
                title: t('users_view.handle_add_user.duplicated_profile.title'),
                user: { ...createdUser, roles: rawRoleId }
              })
            } else if (json.message === DUPLICATED_USER) {
              setDeleteModal({
                footer: false,
                body: (
                  <p style={{ fontWeight: 'bold' }}>
                    <FontAwesomeIcon color='orange' icon='exclamation-circle' />&nbsp;
                    {t('There is already an account linked to this email address')}
                  </p>
                ),
                title: t('Invalid email/username'),
                user: { ...createdUser, roles: rawRoleId }
              })
            }
          })
        }
      })
    })
  }

  const handleEditUser = (updatedUser, stopTableLoading) => {
    if (updatedUser.roles) {
      updatedUser.roles = [roles.filter(r => r.id === updatedUser.roles)[0].apiName]
    }

    requestWithPromise('/api/users/' + updatedUser.id, HttpMethods.PATCH, updatedUser, user, true, true).then(json => {
      // New { account, profiles } contract: the updated profile is rebuilt then merged onto the
      // existing row (to keep lastLogin/isExternal, which the response does not carry).
      const rebuilt = buildUpdatedUserFromResponse(json, updatedUser.id)
      if (rebuilt) {
        setUsers(oldUsers => oldUsers.map(u => u.id === updatedUser.id ? { ...u, ...rebuilt } : u))

        onSuccess(t('user.update.success'))
      }
    }).catch(error => {
      stopTableLoading()
      getErrorBody(error).then(({ message }) => onError(t(message)))
    })
  }

  const removeUser = deletedUser => {
    if (isAdmin) {
      deleteUser(user, deletedUser).then(() => {
        setUsers(oldUsers => oldUsers.filter(u => u.id !== deletedUser.id))
        onSuccess(t('user.delete.success'))
      }).catch(error => {
        onError(t('user.delete.error'))
        console.error(error)
      })
    }
  }

  const handleReviveUser = () => {
    setRevivalLoading(true)

    restoreUserByEmail(user, deleteModal.user).then(json => {
      // The restored profile (backend: same organization only) is the one unknown to the table and
      // attached to this institution — not a sibling profile of another organization.
      const restoredUser = pickAffectedProfile(json, { knownProfileIds: users.map(u => u.id), institutionId: institution.id })

      if (restoredUser) {
        setUsers(oldUsers => [...oldUsers, restoredUser])
      }

      onSuccess(t('users_view.handle_revive_user.restore_user_by_email.on_success'))
    }).catch(error => {
      // 403: the deleted profile belongs to another organization — cross-organization restoration no
      // longer exists, a sibling profile has to be created instead.
      onError(t(error.status === HttpStatuses.FORBIDDEN
        ? 'users_view.handle_revive_user.restore_user_by_email.forbidden'
        : 'user_form_modal.revival.error'))
    })

    handleReviveCancel()
  }

  const handleReviveCancel = () => {
    setDeleteModal({ user: null, body: null, footer: false, title: '' })
    setRevivalLoading(false)
  }

  return (
    <>
      <SmartTable
        columns={columns}
        data={data}
        loading={loading}
        onDataEdit={handleEditUser}
        onDataAdd={handleAddUser}
        addDataText={t('Add user')}
        showAddButtonDisabled={isReadOnly}
        noDataText={t('No user')}
        additionalActions={additionalActions}
        onDataDelete={isAdmin ? removeUser : undefined}
        hideDeleteButtonForIds={[userId]}
      />

      <UserTutoredSectors
        readOnly={isObserver(user)}
        selectedUser={userTutoredSectors}
        onClose={() => setUserTutoredSectors(null)}
      />
      <UserManagedSectors
        selectedUser={selectedUserSectors}
        onClose={() => setSelectedUserSectors(null)}
      />

      {isAdmin &&
        <AdminResetPassword
          userToResetPassword={selectedUserPassword}
          onClose={() => setSelectedUserPassword(null)}
        />}
      <Modal
        title={deleteModal.title}
        visible={deleteModal.user !== null}
        onOk={handleReviveUser}
        onCancel={handleReviveCancel}
        footer={deleteModal.footer
          ? (
            <div>
              <Button onClick={handleReviveCancel}> {t('Cancel')} </Button>
              <Button onClick={handleReviveUser} type='primary' loading={revivalLoading}>
                {t('Restore')}
              </Button>
            </div>
          )
          : null}
      >
        {deleteModal.body}
      </Modal>
    </>
  )
}

export default Users
